/**
 * EF-DOCUMENT-REVIEW-UX-AND-DOCUMENT-SHARING-V1: Document Review Workspace V2
 * 
 * Enhanced workspace with major UX improvements:
 * - Attachment queue sidebar for navigating multiple documents
 * - Resizable viewer/data panels with draggable separator
 * - Scrollable OCR text area with full text copy
 * - Enhanced document viewer (zoom, rotate, PDF support)
 * - Suggested Crew vs Other Crew matching
 * - Auto-advance to next attachment after assignment
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { ScrollArea } from "../ui/scroll-area";
import { Checkbox } from "../ui/checkbox";
// EVENTSFLO-ALIGN-INBOX-DOCUMENT-VIEWER-WITH-AI-ONBOARDING-V1: shared PDF viewer (PDF.js + pan/zoom)
import PdfPanZoomViewer from "../shared/PdfPanZoomViewer";
import {
  Eye,
  FileText,
  User,
  UserPlus,
  Download,
  Loader2,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Copy,
  Check,
  Search,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Sparkles,
  Mail,
  Phone,
  MapPin,
  Calendar,
  CreditCard,
  Globe,
  RefreshCw,
  GripVertical,
  FileImage,
  Files,
  CheckCircle2,
  Clock,
  ArrowRight,
  SkipForward,
  X,
  ImageIcon,
  Wand2,
  AlertCircle,
  CheckCircle,
  HelpCircle,
  Printer,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// EVENTSFLO-FIX-ASSIGN-DOCUMENT-TO-EXISTING-CREW-SEARCH-V1:
// All admin API endpoints used in this workspace are protected by
// require_admin_auth on the backend. Raw fetch() without the
// Authorization header was returning 401, leaving staffList empty —
// which made the "Existing Crew" search always show "No results".
// Mirror the same JWT bearer pattern other admin pages use.
const AUTH_TOKEN_KEY = "eventsflo_auth_token";
const authHeaders = (extra = {}) => {
  let token = "";
  try { token = localStorage.getItem(AUTH_TOKEN_KEY) || ""; } catch {}
  return token
    ? { Authorization: `Bearer ${token}`, ...extra }
    : { ...extra };
};

// EVENTSFLO-INBOX-DOCUMENT-VIEWER-IMAGE-REGRESSION-V1
// /api/inbox/attachments/{id}/file now requires auth. <img src> and
// window.open cannot send Authorization headers, so append ?token=<jwt>
// (backend-supported fallback added in EF-DOCUMENT-STORAGE-SAFE-REMEDIATION-V1).
const withInboxAttachmentToken = (url) => {
  if (!url) return url;
  let token = "";
  try { token = localStorage.getItem(AUTH_TOKEN_KEY) || ""; } catch {}
  if (!token) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}token=${encodeURIComponent(token)}`;
};

// Confidence thresholds for display
const CONFIDENCE_HIGH = 0.85;
const CONFIDENCE_MEDIUM = 0.65;

// ==================== Confidence Badge ====================
function ConfidenceBadge({ confidence }) {
  if (confidence >= CONFIDENCE_HIGH) {
    return (
      <Badge className="text-[9px] bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
        <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
        High
      </Badge>
    );
  }
  if (confidence >= CONFIDENCE_MEDIUM) {
    return (
      <Badge className="text-[9px] bg-amber-500/20 text-amber-400 border-amber-500/30">
        <HelpCircle className="h-2.5 w-2.5 mr-0.5" />
        Review
      </Badge>
    );
  }
  return (
    <Badge className="text-[9px] bg-red-500/20 text-red-400 border-red-500/30">
      <AlertCircle className="h-2.5 w-2.5 mr-0.5" />
      Low
    </Badge>
  );
}

// ==================== Advanced Extracted Fields Panel ====================
function AdvancedExtractedFieldsPanel({ 
  extractedFields, 
  documentType, 
  overallConfidence,
  needsReview,
  onFieldChange,
  onCreateNewCrew,
  onUpdateExistingCrew,
}) {
  // Group fields by category
  const identityFields = ['last_name', 'first_name', 'date_of_birth', 'nationality', 'sex', 'birthplace'];
  const documentFields = ['document_number', 'issue_date', 'expiry_date', 'vitale_number'];
  const contactFields = ['email', 'phone', 'address', 'postal_code', 'city'];
  const cvFields = ['detected_languages', 'detected_qualifications', 'detected_experience_tags', 'raw_cv_text'];
  
  const getFieldIcon = (fieldName) => {
    const icons = {
      last_name: User, first_name: User, date_of_birth: Calendar, nationality: Globe,
      document_number: CreditCard, email: Mail, phone: Phone, address: MapPin,
      vitale_number: CreditCard, expiry_date: Clock, issue_date: Calendar,
    };
    return icons[fieldName] || FileText;
  };
  
  const formatFieldLabel = (fieldName) => {
    return fieldName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };
  
  const renderFieldGroup = (title, fieldKeys, icon) => {
    const Icon = icon;
    const fieldsToShow = fieldKeys.filter(key => extractedFields?.[key]);
    
    if (fieldsToShow.length === 0) return null;
    
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground border-b border-white/5 pb-1">
          <Icon className="h-3.5 w-3.5" />
          {title}
        </div>
        <div className="space-y-2">
          {fieldsToShow.map(fieldKey => {
            const fieldData = extractedFields[fieldKey];
            if (!fieldData) return null;
            
            const FieldIcon = getFieldIcon(fieldKey);
            const isArray = Array.isArray(fieldData.value);
            const displayValue = isArray ? fieldData.value.join(', ') : fieldData.value;
            const confidence = fieldData.confidence || 0;
            const needsFieldReview = fieldData.needs_review;
            
            return (
              <div 
                key={fieldKey} 
                className={cn(
                  "p-2 rounded border",
                  needsFieldReview 
                    ? "bg-amber-500/5 border-amber-500/20" 
                    : "bg-surface-base/30 border-white/5"
                )}
              >
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-[10px] flex items-center gap-1 text-muted-foreground">
                    <FieldIcon className="h-3 w-3" />
                    {formatFieldLabel(fieldKey)}
                  </Label>
                  <ConfidenceBadge confidence={confidence} />
                </div>
                <div className="text-sm font-medium truncate" title={displayValue}>
                  {displayValue || '-'}
                </div>
                {fieldData.normalized_value && fieldData.normalized_value !== fieldData.value && (
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    Normalized: {fieldData.normalized_value}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-3 py-2 border-b border-white/10 bg-surface-elevated/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Extracted Fields</span>
          </div>
          <div className="flex items-center gap-2">
            {documentType && documentType !== 'unknown' && (
              <Badge variant="outline" className="text-[10px]">
                {documentType.replace(/_/g, ' ')}
              </Badge>
            )}
            {needsReview && (
              <Badge className="text-[9px] bg-amber-500/20 text-amber-400 border-amber-500/30">
                Needs Review
              </Badge>
            )}
          </div>
        </div>
        
        {/* Overall confidence bar */}
        {overallConfidence !== undefined && (
          <div className="mt-2">
            <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
              <span>OCR Confidence</span>
              <span>{Math.round(overallConfidence * 100)}%</span>
            </div>
            <div className="h-1.5 bg-surface-base rounded-full overflow-hidden">
              <div 
                className={cn(
                  "h-full rounded-full transition-all",
                  overallConfidence >= CONFIDENCE_HIGH ? "bg-emerald-500" :
                  overallConfidence >= CONFIDENCE_MEDIUM ? "bg-amber-500" : "bg-red-500"
                )}
                style={{ width: `${Math.round(overallConfidence * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
      
      {/* Fields */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-4">
          {(!extractedFields || Object.keys(extractedFields).length === 0) ? (
            <div className="text-center text-muted-foreground py-6">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No fields extracted yet</p>
              <p className="text-xs mt-1">Run advanced OCR to extract structured data</p>
            </div>
          ) : (
            <>
              {renderFieldGroup("Identity", identityFields, User)}
              {renderFieldGroup("Document", documentFields, CreditCard)}
              {renderFieldGroup("Contact", contactFields, Mail)}
              {renderFieldGroup("CV Data", cvFields, FileText)}
            </>
          )}
        </div>
      </ScrollArea>
      
      {/* Actions */}
      {extractedFields && Object.keys(extractedFields).length > 0 && (
        <div className="px-3 py-2 border-t border-white/10 bg-surface-elevated/30 space-y-2">
          <Button 
            variant="default" 
            size="sm" 
            className="w-full"
            onClick={onCreateNewCrew}
            data-testid="create-crew-from-ocr-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Create New Crew from OCR
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full"
            onClick={onUpdateExistingCrew}
            data-testid="update-crew-from-ocr-btn"
          >
            <User className="h-4 w-4 mr-2" />
            Update Existing Crew
          </Button>
        </div>
      )}
    </div>
  );
}

// ==================== Attachment Queue Sidebar ====================
function AttachmentQueueSidebar({ 
  attachments, 
  currentAttachmentId, 
  onSelectAttachment,
  processedIds = [],
}) {
  const getStatusBadge = (attachment) => {
    if (processedIds.includes(attachment.id)) {
      return (
        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Done
        </Badge>
      );
    }
    if (attachment.id === currentAttachmentId) {
      return (
        <Badge className="bg-primary/20 text-primary border-primary/30 text-[10px]">
          <Clock className="h-3 w-3 mr-1" />
          Current
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-muted-foreground text-[10px]">
        Pending
      </Badge>
    );
  };

  const getFileIcon = (attachment) => {
    // EF-FIX-DOCUMENT-PREVIEW-MIME-TYPE-MAPPING-V1: Use includes() for flexible matching
    const mimeType = (attachment.mime_type || "").toLowerCase();
    const filename = (attachment.original_filename || "").toLowerCase();
    
    if (mimeType.includes("pdf") || filename.endsWith(".pdf")) {
      return <FileText className="h-4 w-4 text-red-400" />;
    }
    if (mimeType.includes("image") || filename.match(/\.(jpg|jpeg|png|gif|webp|heic|heif)$/)) {
      return <FileImage className="h-4 w-4 text-blue-400" />;
    }
    return <FileText className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <div className="w-64 border-r border-white/10 flex flex-col bg-surface-base/30">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10 bg-surface-elevated/30">
        <div className="flex items-center gap-2">
          <Files className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Attachment Queue</span>
        </div>
        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          <span>{processedIds.length} / {attachments.length} processed</span>
          {processedIds.length === attachments.length && (
            <CheckCircle2 className="h-3 w-3 text-emerald-400" />
          )}
        </div>
      </div>

      {/* Attachment List */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {attachments.map((attachment, index) => (
            <button
              key={attachment.id}
              onClick={() => onSelectAttachment(attachment)}
              className={cn(
                "w-full p-3 rounded-lg border text-left transition-all",
                attachment.id === currentAttachmentId
                  ? "bg-primary/10 border-primary/40 ring-1 ring-primary/30"
                  : processedIds.includes(attachment.id)
                  ? "bg-emerald-500/5 border-emerald-500/20 hover:border-emerald-500/40"
                  : "bg-muted/20 border-white/10 hover:border-white/30"
              )}
              data-testid={`queue-item-${attachment.id}`}
            >
              <div className="flex items-start gap-2">
                <div className="mt-0.5">{getFileIcon(attachment)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" title={attachment.original_filename}>
                    {attachment.original_filename}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-muted-foreground">#{index + 1}</span>
                    {getStatusBadge(attachment)}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

// ==================== Document Viewer ====================
function DocumentViewer({ attachment, fileUrl, preprocessedImageBase64, showPreprocessed, onTogglePreprocessed }) {
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [fitMode, setFitMode] = useState('fit');
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  
  // EF-FIX-DOCUMENT-PREVIEW-MIME-TYPE-MAPPING-V1: Normalize and use includes()
  const mimeType = (attachment?.mime_type || "").toLowerCase();
  const filename = (attachment?.original_filename || "").toLowerCase();
  
  const isImage = mimeType.includes("image") || 
    filename.match(/\.(jpg|jpeg|png|gif|webp|heic|heif|bmp|tiff?)$/);
  const isPdf = mimeType.includes("pdf") || filename.endsWith(".pdf");
  const isHeic = mimeType.includes("heic") || mimeType.includes("heif") || 
    filename.match(/\.(heic|heif)$/);
  
  const effectiveFileUrl = isHeic
    ? `${fileUrl}${fileUrl && fileUrl.includes('?') ? '&' : '?'}convert=jpeg`
    : fileUrl;
  
  // Determine which image to show
  const displayImageUrl = showPreprocessed && preprocessedImageBase64 
    ? `data:image/png;base64,${preprocessedImageBase64}` 
    : effectiveFileUrl;
  
  const handleZoomIn = () => {
    setFitMode('actual');
    setZoom((z) => Math.min(z + 25, 300));
  };
  
  const handleZoomOut = () => {
    setFitMode('actual');
    setZoom((z) => Math.max(z - 25, 25));
  };
  
  const handleRotate = () => setRotation((r) => (r + 90) % 360);
  
  const handleFitToScreen = () => {
    setFitMode('fit');
    setZoom(100);
  };
  
  // EVENTSFLO-INBOX-ATTACHMENT-DOWNLOAD-FORCE-SAVE-V1:
  // Append disposition=attachment so the backend serves
  // Content-Disposition: attachment, forcing the browser to save instead
  // of opening images inline. We use a programmatic anchor click (with the
  // download attribute as a hint) rather than window.open() so most modern
  // browsers respect the attachment disposition without a tab flash.
  const handleDownload = () => {
    const url = withInboxAttachmentToken(
      `${API_URL}/api/inbox/attachments/${attachment.id}/file?disposition=attachment`
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = attachment?.original_filename || "";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // EVENTSFLO-DOCUMENT-VIEWER-PRINT-ACTION-V1: browser-native print via hidden iframe
  const handlePrint = () => {
    const target = displayImageUrl;
    if (!target) return;
    try {
      const existing = document.getElementById("eventsflo-print-frame");
      if (existing) existing.remove();
      const iframe = document.createElement("iframe");
      iframe.id = "eventsflo-print-frame";
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.setAttribute("aria-hidden", "true");
      iframe.src = target;
      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (err) {
          window.open(target, "_blank", "noopener,noreferrer");
        }
      };
      document.body.appendChild(iframe);
    } catch (err) {
      window.open(target, "_blank", "noopener,noreferrer");
    }
  };
  
  const getImageStyle = () => {
    if (fitMode === 'fit') {
      return {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        transform: `rotate(${rotation}deg)`,
        transition: 'transform 0.2s ease',
      };
    }
    return {
      transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
      transformOrigin: 'top left',
      transition: 'transform 0.2s ease',
    };
  };
  
  useEffect(() => {
    setImageLoading(true);
    setImageError(false);
    setZoom(100);
    setRotation(0);
    setFitMode('fit');
  }, [attachment?.id]);
  
  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-surface-elevated/30">
        <div className="flex items-center gap-1.5">
          {/* EVENTSFLO-ALIGN-INBOX-DOCUMENT-VIEWER-WITH-AI-ONBOARDING-V1:
              the PDF viewer has its own zoom/pagination toolbar, so hide these
              image-only controls when previewing a PDF to avoid duplicate UI. */}
          {!isPdf && (
            <>
              <Button variant="outline" size="sm" onClick={handleZoomOut} disabled={zoom <= 25} title="Zoom Out" className="h-8 w-8 p-0">
                <ZoomOut className="h-3.5 w-3.5" />
              </Button>
              <span className="text-xs font-mono w-12 text-center">{zoom}%</span>
              <Button variant="outline" size="sm" onClick={handleZoomIn} disabled={zoom >= 300} title="Zoom In" className="h-8 w-8 p-0">
                <ZoomIn className="h-3.5 w-3.5" />
              </Button>
              <div className="w-px h-5 bg-white/10 mx-1" />
              <Button 
                variant={fitMode === 'fit' ? 'default' : 'outline'} 
                size="sm" 
                onClick={handleFitToScreen}
                className="h-8 text-xs"
              >
                Fit
              </Button>
            </>
          )}
          {isImage && (
            <Button variant="outline" size="sm" onClick={handleRotate} title="Rotate" className="h-8 w-8 p-0">
              <RotateCw className="h-3.5 w-3.5" />
            </Button>
          )}
          
          {/* EF-ADVANCED-OCR-ONBOARDING-UX-V1: Original/Processed toggle */}
          {isImage && preprocessedImageBase64 && (
            <>
              <div className="w-px h-5 bg-white/10 mx-1" />
              <div className="flex items-center gap-1 bg-surface-base/50 rounded p-0.5">
                <Button 
                  variant={!showPreprocessed ? "default" : "ghost"}
                  size="sm"
                  onClick={() => onTogglePreprocessed(false)}
                  className="h-7 text-[10px] px-2"
                  title="Show original image"
                >
                  <ImageIcon className="h-3 w-3 mr-1" />
                  Original
                </Button>
                <Button 
                  variant={showPreprocessed ? "default" : "ghost"}
                  size="sm"
                  onClick={() => onTogglePreprocessed(true)}
                  className="h-7 text-[10px] px-2"
                  title="Show processed image (enhanced for OCR)"
                >
                  <Wand2 className="h-3 w-3 mr-1" />
                  Processed
                </Button>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isHeic && (
            <Badge variant="outline" className="text-[10px]">HEIC</Badge>
          )}
          {/* EVENTSFLO-DOCUMENT-VIEWER-PRINT-ACTION-V1: image print
              (PDF print lives inside PdfPanZoomViewer's own toolbar) */}
          {isImage && (
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="h-8 text-xs"
              data-testid="document-image-print-btn"
              title="Print image"
            >
              <Printer className="h-3.5 w-3.5 mr-1.5" />
              Print
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleDownload} className="h-8 text-xs">
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Download
          </Button>
        </div>
      </div>
      
      {/* Document Display */}
      {/* EVENTSFLO-INBOX-PDF-PAN-AND-FIT-VIEW-V1: for PDFs, give the viewer
          a definite-height flex slot (no outer overflow / no min-h-full
          growth) so the viewer's own scroll area handles up/down/left/right
          and the toolbar stays pinned. Non-PDF (images, etc.) keep the
          previous flex-1 overflow-auto layout to avoid any regression. */}
      {isPdf ? (
        <div className="flex-1 min-h-0 bg-zinc-900/50" data-testid="document-display-pdf-slot">
          <PdfPanZoomViewer
            src={effectiveFileUrl}
            fileName={attachment?.original_filename}
            height="100%"
          />
        </div>
      ) : (
        <div className="flex-1 overflow-auto bg-zinc-900/50">
          <div className={`min-h-full ${fitMode === 'fit' ? 'flex items-center justify-center p-4' : 'p-4'}`}>
            {isImage ? (
              <>
                {/* EVENTSFLO-ALIGN-INBOX-DOCUMENT-VIEWER-WITH-AI-ONBOARDING-V1:
                    match AI Onboard Crew image rendering — render the <img> directly
                    without hiding it behind a loading/error state machine that could
                    leave PNGs invisible if onLoad never fires. */}
                <img
                  src={displayImageUrl}
                  alt={attachment?.original_filename}
                  style={getImageStyle()}
                  data-testid="document-image-viewer"
                  draggable={false}
                  onLoad={() => { setImageLoading(false); setImageError(false); }}
                  onError={() => { setImageLoading(false); setImageError(true); }}
                />
                {imageError && (
                  <div className="text-center text-muted-foreground mt-4" data-testid="document-image-error">
                    <AlertTriangle className="h-12 w-12 mx-auto mb-2 text-amber-500 opacity-50" />
                    <p>Unable to display image</p>
                    <p className="text-xs mt-1 opacity-70">
                      {attachment?.original_filename || "File"} — the browser could not render this preview.
                      This usually means the session expired or the file is unavailable. Try refreshing the page, or download the file to view it locally.
                    </p>
                    <Button variant="outline" className="mt-3" onClick={handleDownload}>
                      <Download className="h-4 w-4 mr-2" />
                      Download to View
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center text-muted-foreground">
                <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>Preview not available</p>
                <Button variant="outline" className="mt-4" onClick={handleDownload}>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== OCR Text Panel ====================
function OCRTextPanel({ ocrText, loading, ocrFailed, onRefresh }) {
  const [copied, setCopied] = useState(false);
  const textRef = useRef(null);
  
  const handleCopy = async () => {
    if (ocrText) {
      await navigator.clipboard.writeText(ocrText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("OCR text copied to clipboard");
    }
  };
  
  const handleSelectAll = () => {
    if (textRef.current) {
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(textRef.current);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };
  
  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-surface-elevated/30">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">OCR Text</span>
          {loading && (
            <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/30 animate-pulse">
              Running OCR...
            </Badge>
          )}
          {ocrText && !loading && (
            <Badge variant="outline" className="text-[10px]">
              {ocrText.length} chars
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={handleSelectAll} disabled={!ocrText || loading} className="h-7 text-xs">
            Select All
          </Button>
          <Button variant="outline" size="sm" onClick={() => onRefresh(false)} disabled={loading} className="h-7 text-xs">
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Running..." : "Re-scan"}
          </Button>
        </div>
      </div>
      
      {/* OCR Text Content */}
      <div className="flex-1 p-3 flex flex-col gap-2 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-40">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
            <span className="text-sm font-medium text-primary">Running OCR...</span>
            <span className="text-xs text-muted-foreground mt-1">Extracting text from document</span>
          </div>
        ) : ocrText ? (
          <>
            {/* Copy All OCR Text Button - Prominent */}
            <Button 
              variant="default" 
              size="sm" 
              onClick={handleCopy} 
              disabled={loading}
              className="w-full h-9 text-sm font-medium"
              data-testid="copy-all-ocr-text-btn"
            >
              {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              {copied ? "Copied to Clipboard!" : "Copy All OCR Text"}
            </Button>
            
            {/* Resizable OCR Text Area */}
            <div 
              className="relative flex-1"
              style={{ minHeight: '120px' }}
            >
              <textarea
                ref={textRef}
                readOnly
                value={ocrText}
                className="w-full h-full text-sm font-mono whitespace-pre-wrap text-foreground/90 bg-surface-base/30 p-3 rounded border border-white/10 select-text cursor-text resize-y overflow-auto focus:outline-none focus:ring-1 focus:ring-primary/50"
                style={{ 
                  minHeight: '120px', 
                  maxHeight: '600px',
                  resize: 'vertical'
                }}
                data-testid="ocr-text-content"
              />
              {/* Resize hint */}
              <div className="absolute bottom-1 right-3 text-[10px] text-muted-foreground/50 pointer-events-none">
                Drag to resize
              </div>
            </div>
          </>
        ) : ocrFailed ? (
          <div className="text-center text-muted-foreground py-8">
            <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-amber-500 opacity-70" />
            <p className="text-sm font-medium text-amber-400">OCR extraction failed</p>
            <p className="text-xs mt-1">The document could not be processed automatically</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => onRefresh(false)}>
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Retry OCR
            </Button>
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-8">
            <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No OCR text extracted</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => onRefresh(false)}>
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              Run OCR
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== Extracted Data Panel ====================
function ExtractedDataPanel({ extractedData, onChange }) {
  const fields = [
    { key: "last_name", label: "Last Name", icon: User, uppercase: true },
    { key: "first_name", label: "First Name", icon: User },
    { key: "email", label: "Email", icon: Mail, type: "email" },
    { key: "phone", label: "Phone", icon: Phone, type: "tel" },
    { key: "document_number", label: "Doc Number", icon: CreditCard },
    { key: "nationality", label: "Nationality", icon: Globe },
    { key: "birth_date", label: "Birth Date", icon: Calendar, type: "date" },
    { key: "address", label: "Address", icon: MapPin },
  ];
  
  const handleFieldChange = (key, value) => {
    onChange({ ...extractedData, [key]: value });
  };
  
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-surface-elevated/30">
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Extracted Data</span>
        </div>
        <Badge variant="outline" className="text-amber-400 border-amber-500/30 text-[10px]">
          Editable
        </Badge>
      </div>
      
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-3">
          {fields.map((field) => {
            const Icon = field.icon;
            const value = extractedData?.[field.key] || "";
            
            return (
              <div key={field.key} className="space-y-1">
                <Label className="text-xs flex items-center gap-1">
                  <Icon className="h-3 w-3 text-muted-foreground" />
                  {field.label}
                </Label>
                <Input
                  type={field.type || "text"}
                  value={value}
                  onChange={(e) => handleFieldChange(
                    field.key, 
                    field.uppercase ? e.target.value.toUpperCase() : e.target.value
                  )}
                  placeholder={`Enter ${field.label.toLowerCase()}`}
                  className={cn("h-8 text-sm", field.uppercase && "uppercase")}
                  data-testid={`extracted-${field.key}`}
                />
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}

// ==================== Crew Assignment Panel ====================
function CrewAssignmentPanel({ 
  extractedData, 
  staffList, 
  selectedStaffId,
  onSelectStaff,
  onCreateNew,
  documentTypes,
  selectedDocTypeId,
  onSelectDocType,
  isCreatingNew,
  newCrewData,
  onNewCrewDataChange,
  onAssign,
  assigning,
  autoAdvance,
  onAutoAdvanceChange,
  hasMoreAttachments,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestedMatches, setSuggestedMatches] = useState([]);
  const [showOtherCrew, setShowOtherCrew] = useState(false);
  
  // Match algorithm
  useEffect(() => {
    if (!extractedData || !staffList.length) {
      setSuggestedMatches([]);
      return;
    }
    
    const normalize = (str) => (str || "").toLowerCase().trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    
    const lastName = normalize(extractedData.last_name);
    const firstName = normalize(extractedData.first_name);
    const email = normalize(extractedData.email);
    const phone = (extractedData.phone || "").replace(/[\s\-\.]/g, "");
    
    const scoredMatches = staffList.map((staff) => {
      const staffLastName = normalize(staff.last_name);
      const staffFirstName = normalize(staff.first_name);
      const staffEmail = normalize(staff.email);
      const staffPhone = (staff.phone || "").replace(/[\s\-\.]/g, "");
      
      let score = 0;
      let reasons = [];
      
      if (email && staffEmail === email) {
        score += 100;
        reasons.push("Email");
      }
      
      if (phone && phone.length >= 8 && staffPhone.includes(phone)) {
        score += 80;
        reasons.push("Phone");
      }
      
      if (lastName && lastName.length >= 2) {
        if (staffLastName === lastName) {
          score += 50;
          reasons.push("Last name");
        } else if (staffLastName.includes(lastName) || lastName.includes(staffLastName)) {
          score += 30;
          reasons.push("Partial last name");
        }
      }
      
      if (firstName && firstName.length >= 2) {
        if (staffFirstName === firstName) {
          score += 40;
          reasons.push("First name");
        } else if (staffFirstName.includes(firstName) || firstName.includes(staffFirstName)) {
          score += 20;
          reasons.push("Partial first name");
        }
      }
      
      return { ...staff, matchScore: score, matchReasons: reasons };
    });
    
    const matches = scoredMatches
      .filter(s => s.matchScore > 0)
      .sort((a, b) => b.matchScore - a.matchScore)
      .slice(0, 5);
    
    setSuggestedMatches(matches);
    
    if (matches.length === 1 && matches[0].matchScore >= 90 && !selectedStaffId) {
      onSelectStaff(matches[0].id);
    }
  }, [extractedData, staffList, selectedStaffId, onSelectStaff]);
  
  const suggestedIds = new Set(suggestedMatches.map(s => s.id));
  const otherCrew = staffList.filter((staff) => {
    if (suggestedIds.has(staff.id)) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      staff.first_name?.toLowerCase().includes(term) ||
      staff.last_name?.toLowerCase().includes(term) ||
      staff.email?.toLowerCase().includes(term)
    );
  });
  
  const canAssign = selectedDocTypeId && (selectedStaffId || (isCreatingNew && newCrewData.first_name && newCrewData.last_name && newCrewData.email));
  
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-surface-elevated/30">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Assign to Crew</span>
        </div>
      </div>
      
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-4">
          {/* Document Type */}
          <div>
            <Label className="text-xs mb-1.5 block">Document Type *</Label>
            <Select value={selectedDocTypeId} onValueChange={onSelectDocType}>
              <SelectTrigger className="h-9" data-testid="doctype-select">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {documentTypes.map((docType) => (
                  <SelectItem key={docType.id} value={docType.id}>
                    {docType.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Mode Toggle */}
          <div className="flex gap-1.5">
            <Button
              variant={!isCreatingNew ? "default" : "outline"}
              size="sm"
              onClick={() => onCreateNew(false)}
              className="flex-1 h-8 text-xs"
            >
              <Search className="h-3 w-3 mr-1" />
              Existing
            </Button>
            <Button
              variant={isCreatingNew ? "default" : "outline"}
              size="sm"
              onClick={() => onCreateNew(true)}
              className="flex-1 h-8 text-xs"
            >
              <UserPlus className="h-3 w-3 mr-1" />
              New
            </Button>
          </div>
          
          {isCreatingNew ? (
            /* New Crew Form */
            <Card className="bg-emerald-500/10 border-emerald-500/30">
              <CardContent className="p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px]">Last Name *</Label>
                    <Input
                      value={newCrewData.last_name || ""}
                      onChange={(e) => onNewCrewDataChange({ ...newCrewData, last_name: e.target.value.toUpperCase() })}
                      className="h-8 text-sm uppercase"
                      data-testid="new-crew-lastname"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px]">First Name *</Label>
                    <Input
                      value={newCrewData.first_name || ""}
                      onChange={(e) => onNewCrewDataChange({ ...newCrewData, first_name: e.target.value })}
                      className="h-8 text-sm"
                      data-testid="new-crew-firstname"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px]">Email *</Label>
                  <Input
                    type="email"
                    value={newCrewData.email || ""}
                    onChange={(e) => onNewCrewDataChange({ ...newCrewData, email: e.target.value })}
                    className="h-8 text-sm"
                    data-testid="new-crew-email"
                  />
                </div>
                <div>
                  <Label className="text-[10px]">Phone</Label>
                  <Input
                    type="tel"
                    value={newCrewData.phone || ""}
                    onChange={(e) => onNewCrewDataChange({ ...newCrewData, phone: e.target.value })}
                    className="h-8 text-sm"
                  />
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Suggested Matches */}
              {suggestedMatches.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-3.5 w-3.5 text-amber-400" />
                    <span className="text-xs font-medium text-amber-400">Suggested Crew</span>
                    <Badge className="text-[9px] bg-amber-400/10 text-amber-400 border-amber-400/30">
                      {suggestedMatches.length}
                    </Badge>
                  </div>
                  <div className="space-y-1.5">
                    {suggestedMatches.map((staff) => (
                      <button
                        key={staff.id}
                        onClick={() => onSelectStaff(staff.id)}
                        className={cn(
                          "w-full p-2.5 rounded border text-left transition-all",
                          selectedStaffId === staff.id
                            ? "bg-primary/20 border-primary"
                            : "bg-amber-500/5 border-amber-500/20 hover:border-amber-500/40"
                        )}
                        data-testid={`suggested-${staff.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium">{staff.first_name} {staff.last_name}</p>
                            <p className="text-[10px] text-muted-foreground">{staff.email}</p>
                            <p className="text-[10px] text-amber-400/70 mt-0.5">
                              {staff.matchReasons?.join(' · ')}
                            </p>
                          </div>
                          {selectedStaffId === staff.id && <Check className="h-4 w-4 text-primary" />}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Other Crew */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">Other Crew</span>
                </div>
                
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowOtherCrew(true);
                    }}
                    className="h-8 pl-8 text-sm"
                    data-testid="crew-search"
                  />
                </div>
                
                {(searchTerm || showOtherCrew) && (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {otherCrew.slice(0, 15).map((staff) => (
                      <button
                        key={staff.id}
                        onClick={() => onSelectStaff(staff.id)}
                        className={cn(
                          "w-full p-2 rounded border text-left transition-all",
                          selectedStaffId === staff.id
                            ? "bg-primary/20 border-primary"
                            : "bg-muted/20 border-white/10 hover:border-white/30"
                        )}
                        data-testid={`other-${staff.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm">{staff.first_name} {staff.last_name}</p>
                            <p className="text-[10px] text-muted-foreground">{staff.email}</p>
                          </div>
                          {selectedStaffId === staff.id && <Check className="h-4 w-4 text-primary" />}
                        </div>
                      </button>
                    ))}
                    {otherCrew.length === 0 && searchTerm && (
                      <p className="text-center text-xs text-muted-foreground py-4">No results</p>
                    )}
                  </div>
                )}
                
                {!searchTerm && !showOtherCrew && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowOtherCrew(true)}
                    className="w-full text-xs text-muted-foreground h-8"
                  >
                    Browse {staffList.length - suggestedMatches.length} crew
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </ScrollArea>
      
      {/* Action Footer */}
      <div className="p-3 border-t border-white/10 bg-surface-elevated/30 space-y-2">
        {/* Auto-advance option */}
        {hasMoreAttachments && (
          <div className="flex items-center gap-2 p-2 bg-muted/20 rounded border border-white/10">
            <Checkbox
              id="auto-advance"
              checked={autoAdvance}
              onCheckedChange={onAutoAdvanceChange}
            />
            <Label htmlFor="auto-advance" className="text-xs cursor-pointer flex items-center gap-1">
              <SkipForward className="h-3 w-3" />
              Auto-advance to next document
            </Label>
          </div>
        )}
        
        <Button
          onClick={onAssign}
          disabled={assigning || !canAssign}
          className="w-full"
          data-testid="assign-document-btn"
        >
          {assigning ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Check className="h-4 w-4 mr-2" />
          )}
          {isCreatingNew ? "Create & Assign" : "Assign Document"}
          {autoAdvance && hasMoreAttachments && (
            <ArrowRight className="h-4 w-4 ml-2" />
          )}
        </Button>
      </div>
    </div>
  );
}

// ==================== Resizable Panel Separator ====================
function ResizableSeparator({ onDrag }) {
  const [isDragging, setIsDragging] = useState(false);
  
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    
    const handleMouseMove = (moveEvent) => {
      onDrag(moveEvent.clientX);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  return (
    <div
      className={cn(
        "w-1.5 cursor-col-resize flex items-center justify-center bg-transparent hover:bg-primary/20 transition-colors group",
        isDragging && "bg-primary/30"
      )}
      onMouseDown={handleMouseDown}
      data-testid="resize-separator"
    >
      <GripVertical className={cn(
        "h-6 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity",
        isDragging && "opacity-100 text-primary"
      )} />
    </div>
  );
}

// ==================== Main Workspace Component ====================
export function DocumentReviewWorkspaceV2({
  open,
  onOpenChange,
  attachment,
  allAttachments = [],
  onAssignComplete,
}) {
  // Core state
  const [currentAttachment, setCurrentAttachment] = useState(null);
  const [processedIds, setProcessedIds] = useState([]);
  const [fileUrl, setFileUrl] = useState(null);
  const [ocrText, setOcrText] = useState("");
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrFailed, setOcrFailed] = useState(false); // Track if OCR failed to avoid infinite retry
  const [extractedData, setExtractedData] = useState({});
  const [staffList, setStaffList] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [selectedDocTypeId, setSelectedDocTypeId] = useState("");
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newCrewData, setNewCrewData] = useState({});
  const [assigning, setAssigning] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(true);
  
  // EF-ADVANCED-OCR-ONBOARDING-UX-V1: Advanced OCR state
  const [advancedOcrData, setAdvancedOcrData] = useState(null);
  const [preprocessedImageBase64, setPreprocessedImageBase64] = useState(null);
  const [showPreprocessed, setShowPreprocessed] = useState(false);
  const [extractedFields, setExtractedFields] = useState({});
  const [documentType, setDocumentType] = useState(null);
  const [overallConfidence, setOverallConfidence] = useState(0);
  const [needsReview, setNeedsReview] = useState(false);
  
  // Panel sizing
  const [leftPanelWidth, setLeftPanelWidth] = useState(55); // percentage
  const containerRef = useRef(null);
  
  // Get all pending attachments from same context (message or all)
  const queueAttachments = allAttachments.length > 0 ? allAttachments : (attachment ? [attachment] : []);
  
  // Handle panel resize
  const handleResize = useCallback((clientX) => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const sidebarWidth = 256; // 16rem = 256px
    const availableWidth = rect.width - sidebarWidth;
    const newLeftWidth = ((clientX - rect.left - sidebarWidth) / availableWidth) * 100;
    setLeftPanelWidth(Math.min(Math.max(newLeftWidth, 30), 70));
  }, []);
  
  // Load file URL
  useEffect(() => {
    if (currentAttachment?.id) {
      setFileUrl(withInboxAttachmentToken(`${API_URL}/api/inbox/attachments/${currentAttachment.id}/file`));
    }
  }, [currentAttachment]);
  
  // Load staff list
  const loadStaffList = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/staff?limit=500`, {
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setStaffList(data.items || data.staff || []);
      }
    } catch (err) {
      console.error("Load staff error:", err);
    }
  }, []);
  
  // Load document types
  const loadDocumentTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/document-types`, {
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setDocumentTypes(data || []);
      }
    } catch (err) {
      console.error("Load document types error:", err);
    }
  }, []);
  
  // Run OCR
  const runOCR = useCallback(async (isAutoTrigger = false) => {
    if (!currentAttachment?.id) return;
    
    setOcrLoading(true);
    setOcrFailed(false);
    try {
      const res = await fetch(`${API_URL}/api/inbox/attachments/${currentAttachment.id}/ocr`, {
        method: "POST",
        headers: authHeaders(),
      });
      
      if (res.ok) {
        const data = await res.json();
        setOcrText(data.ocr_text || "");
        
        if (data.extracted_data) {
          setExtractedData(data.extracted_data);
          setNewCrewData({
            last_name: data.extracted_data.last_name || "",
            first_name: data.extracted_data.first_name || "",
            email: data.extracted_data.email || "",
            phone: data.extracted_data.phone || "",
            address: data.extracted_data.address || "",
          });
        }
        
        // Only show toast for manual triggers
        if (!isAutoTrigger) {
          toast.success("OCR scan completed");
        }
      } else {
        // OCR failed - mark as failed so we don't auto-retry
        setOcrFailed(true);
        const errorData = await res.json().catch(() => ({}));
        console.error("OCR failed:", errorData);
        if (!isAutoTrigger) {
          toast.error(errorData.detail || "OCR scan failed");
        }
      }
    } catch (err) {
      console.error("OCR error:", err);
      setOcrFailed(true);
      if (!isAutoTrigger) {
        toast.error("OCR scan failed");
      }
    } finally {
      setOcrLoading(false);
    }
  }, [currentAttachment]);
  
  // EF-ADVANCED-OCR-ONBOARDING-UX-V1: Run advanced OCR with preprocessing and field extraction
  const runAdvancedOCR = useCallback(async (isAutoTrigger = false) => {
    if (!currentAttachment?.asset_id && !currentAttachment?.id) return;
    
    setOcrLoading(true);
    setOcrFailed(false);
    
    try {
      // Use asset_id if available, otherwise fall back to attachment id
      const assetId = currentAttachment.asset_id || currentAttachment.id;
      
      const res = await fetch(`${API_URL}/api/document-assets/${assetId}/ocr-advanced?force_reprocess=false`, {
        method: "POST",
        headers: authHeaders(),
      });
      
      if (res.ok) {
        const data = await res.json();
        
        // Update OCR text
        setOcrText(data.ocr_raw_text || "");
        
        // Update advanced OCR data
        setAdvancedOcrData(data);
        setPreprocessedImageBase64(data.preprocessed_image_base64 || null);
        setExtractedFields(data.extracted_fields || data.ocr_extracted_data?.extracted_fields || {});
        setDocumentType(data.document_type || data.ocr_extracted_data?.document_type || null);
        setOverallConfidence(data.overall_confidence || data.ocr_extracted_data?.overall_confidence || 0);
        setNeedsReview(data.needs_review || data.ocr_extracted_data?.needs_review || false);
        
        // Also update legacy extracted data for crew matching
        if (data.extracted_fields) {
          const legacyData = {};
          for (const [key, fieldData] of Object.entries(data.extracted_fields)) {
            if (typeof fieldData === 'object' && fieldData.value !== undefined) {
              legacyData[key] = Array.isArray(fieldData.value) ? fieldData.value.join(', ') : fieldData.value;
            }
          }
          setExtractedData(legacyData);
          
          // Pre-fill new crew data
          setNewCrewData({
            last_name: legacyData.last_name || "",
            first_name: legacyData.first_name || "",
            email: legacyData.email || "",
            phone: legacyData.phone || "",
            address: legacyData.address || "",
          });
        }
        
        if (!isAutoTrigger) {
          toast.success(`Advanced OCR completed (${Math.round(data.overall_confidence * 100)}% confidence)`);
        }
      } else {
        setOcrFailed(true);
        const errorData = await res.json().catch(() => ({}));
        console.error("Advanced OCR failed:", errorData);
        if (!isAutoTrigger) {
          toast.error(errorData.detail || "Advanced OCR failed");
        }
      }
    } catch (err) {
      console.error("Advanced OCR error:", err);
      setOcrFailed(true);
      if (!isAutoTrigger) {
        toast.error("Advanced OCR failed");
      }
    } finally {
      setOcrLoading(false);
    }
  }, [currentAttachment]);
  
  // Handle create new crew from OCR
  const handleCreateNewCrewFromOCR = useCallback(async () => {
    if (!extractedFields || Object.keys(extractedFields).length === 0) {
      toast.error("No extracted fields available");
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/staff/prefill-from-ocr`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(extractedFields),
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.prefill_data) {
          setNewCrewData(data.prefill_data);
          setIsCreatingNew(true);
          
          if (data.low_confidence_fields?.length > 0) {
            toast.info(`Pre-filled ${data.field_count} fields. Review: ${data.low_confidence_fields.join(', ')}`);
          } else {
            toast.success(`Pre-filled ${data.field_count} fields from OCR`);
          }
        }
      }
    } catch (err) {
      console.error("Prefill from OCR error:", err);
      toast.error("Failed to prefill from OCR");
    }
  }, [extractedFields]);
  
  // Handle update existing crew from OCR
  // EVENTSFLO-WIRE-CV-EXTRACTION-INTO-SUITABILITY-V1: actually push extracted
  // CV/experience fields into the selected crew's Suitability section
  // (additive merge — never destroys existing roles/qualifications/languages/tags).
  const handleUpdateExistingCrewFromOCR = useCallback(async () => {
    if (!selectedStaffId) {
      toast.info("Please select an existing crew member first");
      return;
    }
    if (!extractedFields || Object.keys(extractedFields).length === 0) {
      toast.error("No extracted CV fields available");
      return;
    }
    // Only push the CV-suitability subset to the suitability endpoint.
    const cvSubset = {};
    ["detected_languages", "detected_qualifications", "detected_experience_tags"].forEach((k) => {
      if (extractedFields[k]) cvSubset[k] = extractedFields[k];
    });
    if (Object.keys(cvSubset).length === 0) {
      toast.info("No CV-derived languages, qualifications or experience tags were detected");
      return;
    }
    setIsCreatingNew(false);
    try {
      const res = await fetch(`${API_URL}/api/staff/${selectedStaffId}/apply-cv-extraction`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ extracted_fields: cvSubset }),
      });
      if (!res.ok) {
        let err = null;
        try { err = await res.json(); } catch {}
        throw new Error(err?.detail || `Update failed (${res.status})`);
      }
      const data = await res.json();
      const a = data.applied || {};
      const lang = (a.languages_added || []).length;
      const tags = (a.experience_tags_added || []).length;
      const quals = (a.qualifications_added || []).length;
      const unmatched = (data.unmatched_qualifications || []).length;
      if (lang + tags + quals === 0) {
        toast.info(
          unmatched > 0
            ? `Nothing to add. ${unmatched} qualification(s) didn't match the master list.`
            : "Nothing new to add — crew already has these suitability values."
        );
      } else {
        const parts = [];
        if (lang) parts.push(`${lang} language(s)`);
        if (tags) parts.push(`${tags} experience tag(s)`);
        if (quals) parts.push(`${quals} qualification(s) (pending verification)`);
        toast.success(`Suitability updated: ${parts.join(", ")}` + (unmatched ? ` · ${unmatched} unmatched` : ""));
      }
      // refresh local staff list so suggested matches reflect the latest crew row
      loadStaffList();
    } catch (err) {
      console.error("Apply CV extraction failed:", err);
      toast.error(err.message || "Failed to update Suitability from CV");
    }
  }, [selectedStaffId, extractedFields, loadStaffList]);
  
  // Initialize when opening
  useEffect(() => {
    if (open) {
      loadStaffList();
      loadDocumentTypes();
      setCurrentAttachment(attachment);
      setProcessedIds([]);
    }
  }, [open, attachment, loadStaffList, loadDocumentTypes]);
  
  // Reset state when current attachment changes
  useEffect(() => {
    if (currentAttachment) {
      setSelectedStaffId("");
      setSelectedDocTypeId("");
      setIsCreatingNew(false);
      setNewCrewData({});
      setOcrFailed(false);
      
      // Reset advanced OCR state
      setAdvancedOcrData(null);
      setPreprocessedImageBase64(null);
      setShowPreprocessed(false);
      setExtractedFields({});
      setDocumentType(null);
      setOverallConfidence(0);
      setNeedsReview(false);
      
      // Check if attachment already has OCR data
      const existingOcrText = currentAttachment.ocr_text;
      const existingExtractedData = currentAttachment.ocr_extracted_data;
      
      if (existingOcrText) {
        // Use existing OCR data
        setOcrText(existingOcrText);
        if (existingExtractedData) {
          setExtractedData(existingExtractedData);
          // Also populate advanced OCR data if available
          if (existingExtractedData.extracted_fields) {
            setExtractedFields(existingExtractedData.extracted_fields);
            setDocumentType(existingExtractedData.document_type);
            setOverallConfidence(existingExtractedData.overall_confidence || 0);
            setNeedsReview(existingExtractedData.needs_review || false);
            setPreprocessedImageBase64(existingExtractedData.preprocessed_image_base64);
          }
          setNewCrewData({
            last_name: existingExtractedData.last_name || "",
            first_name: existingExtractedData.first_name || "",
            email: existingExtractedData.email || "",
            phone: existingExtractedData.phone || "",
            address: existingExtractedData.address || "",
          });
        }
      } else {
        // No existing OCR data - clear state and auto-trigger advanced OCR for images/PDFs
        setOcrText("");
        setExtractedData({});
        
        const mimeType = currentAttachment.mime_type?.toLowerCase() || "";
        const filename = currentAttachment.original_filename?.toLowerCase() || "";
        const isImage = mimeType.startsWith("image/") || filename.match(/\.(jpg|jpeg|png|gif|webp|heic|heif|bmp|tiff?)$/);
        const isPdf = mimeType.includes("pdf") || filename.endsWith(".pdf");
        
        if (isImage || isPdf) {
          // Auto-trigger advanced OCR (pass true to indicate auto-trigger for silent failure)
          runAdvancedOCR(true);
        }
      }
    }
  }, [currentAttachment, runAdvancedOCR]);
  
  // Handle select attachment from queue
  const handleSelectAttachment = (att) => {
    setCurrentAttachment(att);
  };
  
  // Find next unprocessed attachment
  const findNextAttachment = () => {
    const currentIndex = queueAttachments.findIndex(a => a.id === currentAttachment?.id);
    for (let i = currentIndex + 1; i < queueAttachments.length; i++) {
      if (!processedIds.includes(queueAttachments[i].id)) {
        return queueAttachments[i];
      }
    }
    // Wrap around
    for (let i = 0; i < currentIndex; i++) {
      if (!processedIds.includes(queueAttachments[i].id)) {
        return queueAttachments[i];
      }
    }
    return null;
  };
  
  const hasMoreAttachments = queueAttachments.some(a => !processedIds.includes(a.id) && a.id !== currentAttachment?.id);
  
  // Handle assignment
  const handleAssign = async () => {
    if (!selectedDocTypeId) {
      toast.error("Please select a document type");
      return;
    }
    
    if (!isCreatingNew && !selectedStaffId) {
      toast.error("Please select a crew member");
      return;
    }
    
    if (isCreatingNew && (!newCrewData.first_name || !newCrewData.last_name || !newCrewData.email)) {
      toast.error("Please fill required fields");
      return;
    }
    
    setAssigning(true);
    
    try {
      let staffId = selectedStaffId;
      
      // Create new crew if needed
      if (isCreatingNew) {
        const createRes = await fetch(`${API_URL}/api/staff`, {
          method: "POST",
          headers: authHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({
            first_name: newCrewData.first_name,
            last_name: newCrewData.last_name,
            email: newCrewData.email,
            phone: newCrewData.phone || null,
            address_line_1: newCrewData.address || null,
            status: "active",
            employment_type: "casual",
          }),
        });
        
        if (!createRes.ok) {
          // EF-FIX-CREW-PROFILE-UPDATE-JSON-STREAM-ERROR-V1: Single-read pattern
          let errorData = null;
          try {
            errorData = await createRes.json();
          } catch {}
          throw new Error(errorData?.detail || "Failed to create crew");
        }
        
        const newStaff = await createRes.json();
        staffId = newStaff.id;
        toast.success(`Created: ${newCrewData.first_name} ${newCrewData.last_name}`);
        
        // Refresh staff list
        loadStaffList();
      }
      
      // Assign document
      const assignRes = await fetch(`${API_URL}/api/inbox/attachments/${currentAttachment.id}/assign`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          staff_id: staffId,
          document_type_id: selectedDocTypeId,
          notes: "Assigned via Document Review Workspace V2",
        }),
      });
      
      if (!assignRes.ok) {
        // EF-FIX-CREW-PROFILE-UPDATE-JSON-STREAM-ERROR-V1: Single-read pattern
        let errorData = null;
        try {
          errorData = await assignRes.json();
        } catch {}
        throw new Error(errorData?.detail || "Failed to assign");
      }
      
      toast.success("Document assigned successfully");
      
      // Mark as processed
      setProcessedIds(prev => [...prev, currentAttachment.id]);
      
      // Auto-advance
      if (autoAdvance) {
        const next = findNextAttachment();
        if (next) {
          setCurrentAttachment(next);
        } else {
          // All done
          toast.success("All attachments processed!");
          onOpenChange(false);
          onAssignComplete?.();
        }
      } else {
        onOpenChange(false);
        onAssignComplete?.();
      }
      
    } catch (err) {
      console.error("Assignment error:", err);
      toast.error(err.message);
    } finally {
      setAssigning(false);
    }
  };
  
  if (!currentAttachment) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="bg-card border-white/10 p-0 overflow-hidden flex flex-col [&>button]:hidden"
        style={{ maxWidth: "95vw", width: "95vw", maxHeight: "92vh", height: "92vh" }}
      >
        {/* Header */}
        <DialogHeader className="px-4 py-3 border-b border-white/10 bg-surface-elevated/30 shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" />
              Document Review
              <Badge variant="outline" className="ml-2 max-w-[300px] truncate">
                {currentAttachment.original_filename}
              </Badge>
            </DialogTitle>
            
            {/* Header Actions - Right side with proper spacing */}
            <div className="flex items-center gap-3" style={{ marginRight: '8px' }}>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span data-testid="processed-counter">{processedIds.length} / {queueAttachments.length} processed</span>
                {processedIds.length === queueAttachments.length && (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Complete
                  </Badge>
                )}
              </div>
              
              {/* Explicit Close Button */}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-full hover:bg-white/10"
                onClick={() => onOpenChange(false)}
                data-testid="document-review-close-btn"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        {/* Main Content */}
        <div ref={containerRef} className="flex-1 flex overflow-hidden">
          {/* Attachment Queue Sidebar */}
          {queueAttachments.length > 1 && (
            <AttachmentQueueSidebar
              attachments={queueAttachments}
              currentAttachmentId={currentAttachment.id}
              onSelectAttachment={handleSelectAttachment}
              processedIds={processedIds}
            />
          )}
          
          {/* Left Panel - Document Viewer + OCR + Extracted Data */}
          {/* EVENTSFLO-INBOX-PDF-BOTTOM-CLIP-FIX-V1: add min-h-0 across the
              flex chain so the inner PDF viewport is bounded by the modal's
              available height instead of growing to its content's natural
              height (which previously pushed the PDF bottom below the modal). */}
          <div 
            className="flex flex-col border-r border-white/10 min-h-0 min-w-0"
            style={{ width: `${leftPanelWidth}%` }}
          >
            <Tabs defaultValue="viewer" className="flex-1 flex flex-col min-h-0">
              <TabsList className="mx-3 mt-3 mb-0 bg-surface-elevated/30 border border-white/5 w-fit">
                <TabsTrigger value="viewer" className="gap-1.5 text-xs">
                  <Eye className="h-3.5 w-3.5" />
                  Document
                </TabsTrigger>
                <TabsTrigger value="ocr" className="gap-1.5 text-xs">
                  <FileText className="h-3.5 w-3.5" />
                  OCR Text
                </TabsTrigger>
                <TabsTrigger value="fields" className="gap-1.5 text-xs">
                  <Wand2 className="h-3.5 w-3.5" />
                  Fields
                  {needsReview && (
                    <span className="ml-1 w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  )}
                </TabsTrigger>
                <TabsTrigger value="extracted" className="gap-1.5 text-xs">
                  <CreditCard className="h-3.5 w-3.5" />
                  Data
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="viewer" className="flex-1 m-0 p-0 min-h-0 flex flex-col">
                <DocumentViewer 
                  attachment={currentAttachment} 
                  fileUrl={fileUrl}
                  preprocessedImageBase64={preprocessedImageBase64}
                  showPreprocessed={showPreprocessed}
                  onTogglePreprocessed={setShowPreprocessed}
                />
              </TabsContent>
              
              <TabsContent value="ocr" className="flex-1 m-0 p-0 min-h-0">
                <OCRTextPanel ocrText={ocrText} loading={ocrLoading} ocrFailed={ocrFailed} onRefresh={runAdvancedOCR} />
              </TabsContent>
              
              <TabsContent value="fields" className="flex-1 m-0 p-0 min-h-0">
                <AdvancedExtractedFieldsPanel
                  extractedFields={extractedFields}
                  documentType={documentType}
                  overallConfidence={overallConfidence}
                  needsReview={needsReview}
                  onFieldChange={() => {}}
                  onCreateNewCrew={handleCreateNewCrewFromOCR}
                  onUpdateExistingCrew={handleUpdateExistingCrewFromOCR}
                />
              </TabsContent>
              
              <TabsContent value="extracted" className="flex-1 m-0 p-0 min-h-0">
                <ExtractedDataPanel extractedData={extractedData} onChange={setExtractedData} />
              </TabsContent>
            </Tabs>
          </div>
          
          {/* Resizable Separator */}
          <ResizableSeparator onDrag={handleResize} />
          
          {/* Right Panel - Crew Assignment */}
          <div className="flex-1 min-w-[280px]">
            <CrewAssignmentPanel
              extractedData={extractedData}
              staffList={staffList}
              selectedStaffId={selectedStaffId}
              onSelectStaff={setSelectedStaffId}
              onCreateNew={setIsCreatingNew}
              documentTypes={documentTypes}
              selectedDocTypeId={selectedDocTypeId}
              onSelectDocType={setSelectedDocTypeId}
              isCreatingNew={isCreatingNew}
              newCrewData={newCrewData}
              onNewCrewDataChange={setNewCrewData}
              onAssign={handleAssign}
              assigning={assigning}
              autoAdvance={autoAdvance}
              onAutoAdvanceChange={setAutoAdvance}
              hasMoreAttachments={hasMoreAttachments}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DocumentReviewWorkspaceV2;
